import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { uniqBy } from 'lodash-es';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { PortfolioTaskListComponent } from '@features/portfolio-task-list/portfolio-task-list.component';

import { UserTasksQuery } from '@models/user-tasks';

import { IconComponent } from '@shared/components/icon/icon.component';
import { listUserTasksQuery, UserTaskType } from '@shared/services/gql.service';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { NO_TASKS_MESSAGE } from './tasks.const';

interface TaskFormValues {
  trial: string;
  type: UserTaskType;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    NgSelectComponent,
    PortfolioTaskListComponent,
    AsyncPipe,
  ],
  selector: 'aux-portfolio-tasks',
  templateUrl: './portfolio-tasks.component.html',
})
export class PortfolioTasksComponent implements OnInit {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly userTasksQuery = inject(UserTasksQuery);

  filteredUserListTasks$ = new BehaviorSubject<listUserTasksQuery[]>([]);
  filterForm = this.formBuilder.group({
    trial: null,
    type: null,
  });
  noTasksMessages = NO_TASKS_MESSAGE;
  originalUserListTasks$ = this.userTasksQuery.selectAll();
  taskTypeOptions: Option<UserTaskType>[] = [
    {
      label: 'Change Orders',
      value: UserTaskType.CHANGE_ORDER,
    },
    {
      label: 'Forecast Methodology',
      value: UserTaskType.FORECAST_VENDOR_COSTS,
    },
    {
      label: 'Invoices',
      value: UserTaskType.INVOICE,
    },
    {
      label: 'Period Close',
      value: UserTaskType.PERIOD_CLOSE,
    },
    {
      label: 'Vendor Estimates',
      value: UserTaskType.VENDOR_ESTIMATE,
    },
  ];
  trialOptions: Option[] = [];

  ngOnInit(): void {
    this.originalUserListTasks$
      .pipe(
        takeUntilDestroyed(),
        map((tasks) => this.filterTasks(tasks, {})),
        tap((tasks) => {
          this.filteredUserListTasks$.next(tasks);
          this.trialOptions = this.getTrialOptions(tasks);
        })
      )
      .subscribe();

    combineLatest([this.filterForm.valueChanges, this.originalUserListTasks$])
      .pipe(
        takeUntilDestroyed(),
        map(
          ([formValues, originalUserListTasks]: [Partial<TaskFormValues>, listUserTasksQuery[]]) =>
            this.filterTasks(originalUserListTasks, formValues)
        ),
        tap((tasks) => {
          this.filteredUserListTasks$.next(tasks);
        })
      )
      .subscribe();
  }

  filterTasks(
    taskList: listUserTasksQuery[],
    formValues: Partial<TaskFormValues>
  ): listUserTasksQuery[] {
    return taskList
      .filter(({ trial_id }) => (formValues.trial ? trial_id === formValues.trial : true))
      .filter(({ user_task }) => (formValues.type ? user_task === formValues.type : true))
      .sort((task1, task2) => Utils.dateSort(task1.create_date, task2.create_date));
  }

  getTrialOptions(taskList: listUserTasksQuery[]) {
    const trialsOptions = taskList.map(({ trial_id, trial_name }) => ({
      label: trial_name,
      value: trial_id,
    }));

    return uniqBy(trialsOptions, 'label').sort((trial, trial2) =>
      Utils.alphaNumSort(trial.label, trial2.label)
    );
  }
}
